<script setup>
import { computed, inject, toValue } from 'vue';
import { useField, FormContextKey } from 'vee-validate';

const props = defineProps({
    id: {
        type: String,
        default: undefined
    },
    // Classes da coluna em volta do campo.
    classeColuna: {
        type: String,
        default: 'col-12'
    },
    // Nome do campo no schema do useForm. Quando informado, o componente se conecta
    // sozinho ao formulário pai (valor e erro), dispensando v-model e :erro.
    name: {
        type: String,
        default: null
    },
    erro: {
        type: String,
        default: null
    },
    modelValue: {
        type: Number,
        default: null
    },
    titulo: {
        type: String,
        default: ''
    },
    placeholder: {
        type: String,
        default: '0,00'
    },
    // Exibe o valor como moeda (R$).
    moeda: {
        type: Boolean,
        default: false
    },
    casasDecimais: {
        type: Number,
        default: 2
    },
    // Casas decimais sempre exibidas. Sem valor, usa `casasDecimais` (ex.: 0 mostra "1" e ainda aceita "1,5").
    casasDecimaisMinimas: {
        type: Number,
        default: null
    },
    // Texto exibido após o valor (ex.: ' horas').
    sufixo: {
        type: String,
        default: undefined
    },
    min: {
        type: Number,
        default: undefined
    },
    max: {
        type: Number,
        default: undefined
    },
    disabled: {
        type: Boolean,
        default: false
    },
    // Normalmente dispensável: a obrigatoriedade vem do schema. Serve para forçar o
    // asterisco em regra condicional (`when`), que o schema não expõe estaticamente.
    obrigatorio: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['update:modelValue', 'change']);

// Getter para acompanhar mudanças no nome (ex.: índice de item em useFieldArray após remover outro).
const campoForm = props.name ? useField(() => props.name) : null;

const valor = computed({
    get: () => (campoForm ? campoForm.value.value : props.modelValue) ?? null,
    set: (value) => {
        if (campoForm) campoForm.value.value = value;
        emit('update:modelValue', value);
        emit('change', value);
    }
});

const mensagemErro = computed(() => props.erro ?? campoForm?.errorMessage.value ?? null);

const formulario = inject(FormContextKey, null);

// O asterisco de obrigatório sai da própria regra do schema (ex.: regraValorNumerico()
// vs regraValorNumerico({ obrigatorio: false })), para não ser declarado duas vezes.
const obrigatorioDoSchema = computed(() => {
    if (!props.name) return null;

    try {
        const campo = toValue(formulario?.schema)?.describe?.()?.fields?.[props.name];
        return campo ? campo.optional === false : null;
    } catch {
        return null;
    }
});

// O schema só acrescenta o asterisco: a prop continua vencendo, porque um `when`
// aparece como opcional no describe() e ainda assim é obrigatório em tempo de uso.
const obrigatorio = computed(() => props.obrigatorio || obrigatorioDoSchema.value === true);
</script>

<template>
    <div :class="[{ 'field mb-2': props.titulo }, props.classeColuna]">
        <SmallTitle v-if="props.titulo" :titulo="props.titulo" :obrigatorio="obrigatorio" />
        <!-- O slot #acao fica grudado à direita do campo (ex.: botão de calcular). -->
        <div :class="{ 'p-inputgroup': $slots.acao }">
            <InputNumber :inputId="props.id" v-model="valor" locale="pt-BR" class="text-sm" inputClass="text-sm"
                :mode="props.moeda ? 'currency' : 'decimal'" :currency="props.moeda ? 'BRL' : undefined"
                :minFractionDigits="props.casasDecimaisMinimas ?? props.casasDecimais" :maxFractionDigits="props.casasDecimais" :min="props.min"
                :max="props.max" :suffix="props.sufixo" :disabled="props.disabled" :placeholder="props.placeholder"
                :class="{ 'p-invalid': mensagemErro }" />
            <slot name="acao" />
        </div>
        <SmallError v-if="mensagemErro" :error="mensagemErro" />
        <!-- Texto de apoio abaixo do campo (ex.: valor calculado automaticamente). -->
        <slot />
    </div>
</template>
